import React, { useEffect, useContext, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { DataContext } from '../context/DataContext';
import { Button, Box, Typography } from '@mui/material';
import { styled } from '@mui/system';
import Header from '../components/Header';

const ServicePage = () => {
  const { id } = useParams();
  const { serviceData, loadServiceData } = useContext(DataContext);

  const [isSessionActive, setIsSessionActive] = useState(false);
  const [dataChannel, setDataChannel] = useState(null);
  const [events, setEvents] = useState([]);
  const [output, setOutput] = useState([]);
  const peerConnection = useRef(null);
  const audioElement = useRef(null);

  const [functionAdded, setFunctionAdded] = useState(false);
  const [functionCallOutput, setFunctionCallOutput] = useState(null);
  const [approvedServices, setApprovedServices] = useState([]);
  const [imageURL, setImageURL] = useState(null);

  useEffect(() => {
    loadServiceData(id);
  }, [id, loadServiceData]);

  useEffect(() => {
    if (serviceData && serviceData.vehicle && serviceData.vehicle.id && !imageURL) {
      const imageServerUrl = process.env.REACT_APP_IMAGE_SERVER_URL || 'http://localhost:8069/vehicle/image/';
      setImageURL(`${imageServerUrl}${serviceData.vehicle.id}`);
    }
  }, [serviceData, imageURL]);

  useEffect(() => {
    if (dataChannel) {
      dataChannel.addEventListener("message", (e) => {
        const message = JSON.parse(e.data);
        console.log("Server event:", message);
        setEvents((prev) => [message, ...prev]);
      });

      dataChannel.addEventListener("open", () => {
        console.log("Data channel opened");
        setEvents([]);
      });
    }
  }, [dataChannel]);

  useEffect(() => {
    if (!events || events.length === 0) return;

    const firstEvent = events[events.length - 1];
    if (!functionAdded && firstEvent.type === "session.created") {
      // sendClientEvent(sessionUpdate);
      setFunctionAdded(true);
    }

    const mostRecentEvent = events[0];
    if (mostRecentEvent.type === "response.done" && mostRecentEvent.response?.output) {
      mostRecentEvent.response.output.forEach((output) => {
        if (output.type === "function_call" && output.name === "approve_services") {
          setFunctionCallOutput(output);
          setOutput((prev) => [output, ...prev]); // Add output to the output array
          const approvedService = JSON.parse(output.arguments);
          setApprovedServices((prev) => [...prev, approvedService]); // Add approved service to the array
          console.log("Function call output:", output);
          setTimeout(() => {
            sendClientEvent({
              type: "response.create",
              response: {
                instructions: "Service approval is noted, ask the customer if there are other questions",
              },
            });
          }, 500);
        }
      });
    }
  }, [events, functionAdded]);

  useEffect(() => {
    if (!isSessionActive) {
      setFunctionAdded(false);
      setFunctionCallOutput(null);
    }
  }, [isSessionActive]);

  async function startSession() {
    const relationId = serviceData?.relation_id || id;
    const tokenResponse = await fetch(`${process.env.REACT_APP_SOCKET_SERVER_URL}/token?relation_id=${relationId}`);
    console.log('Token response:', tokenResponse);
    const data = await tokenResponse.json();
    const EPHEMERAL_KEY = data.client_secret.value;

    // Create a peer connection
    const pc = new RTCPeerConnection();

    // Set up to play remote audio from the model
    audioElement.current = document.createElement("audio");
    audioElement.current.autoplay = true;
    pc.ontrack = (e) => (audioElement.current.srcObject = e.streams[0]);

    // Add local audio track for microphone input in the browser
    const ms = await navigator.mediaDevices.getUserMedia({
      audio: true,
    });
    pc.addTrack(ms.getTracks()[0]);

    // Set up data channel for sending and receiving events
    const dc = pc.createDataChannel("oai-events");
    setDataChannel(dc);

    // Start the session using the Session Description Protocol (SDP)
    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);

    const baseUrl = "https://api.openai.com/v1/realtime";
    const model = "gpt-4o-realtime-preview-2024-12-17";
    const sdpResponse = await fetch(`${baseUrl}?model=${model}`, {
      method: "POST",
      body: offer.sdp,
      headers: {
        Authorization: `Bearer ${EPHEMERAL_KEY}`,
        "Content-Type": "application/sdp",
      },
    });

    const answer = {
      type: "answer",
      sdp: await sdpResponse.text(),
    };
    await pc.setRemoteDescription(answer);

    setIsSessionActive(true);
    sendTextMessage("Hi");
    peerConnection.current = pc;
  }

  function stopSession() {
    if (dataChannel) {
      dataChannel.close();
    }
    if (peerConnection.current) {
      peerConnection.current.close();
    }

    setIsSessionActive(false);
    setDataChannel(null);
    peerConnection.current = null;
  }

  function sendClientEvent(message) {
    if (dataChannel) {
      message.event_id = message.event_id || crypto.randomUUID();
      dataChannel.send(JSON.stringify(message));
      setEvents((prev) => [message, ...prev]);
      console.log("Client event:", message);
    } else {
      console.error("No data channel available", message);
    }
  }

  function sendTextMessage(message) {
    const event = {
      type: "conversation.item.create",
      item: {
        type: "message",
        role: "user",
        content: [
          { type: "input_text", text: message },
        ],
      },
    };
    sendClientEvent(event);
    sendClientEvent({ type: "response.create" });
  }

  if (!serviceData) {
    return <div>Loading...</div>;
  }

  const { customer, vehicle, services } = serviceData;

  return (
    <Box sx={{ padding: 2 }}>
      <Typography variant="h6">{customer.name}</Typography>
      <Typography variant="body1">{vehicle.make} {vehicle.model}</Typography>
      <Typography variant="body1">License Plate: {vehicle.license_plate}</Typography>
      <Box component="img" src={imageURL} alt={vehicle.model} sx={{ width: '80%', mt: 2, display: 'block', margin: '2 auto' }} />

      <Typography variant="h6" sx={{ mt: 2 }}>Approved Services</Typography>
      {approvedServices.map((service, index) => (
        <Box key={index} sx={{ mt: 1 }}>
          <Typography variant="subtitle1">{service.name}</Typography>
          <Typography variant="body2">Price: {service.code}</Typography>
        </Box>
      ))}

      <Typography variant="h6" sx={{ mt: 2 }}>Upcoming Services</Typography>
      {services.map((service, index) => (
        <Box key={index} sx={{ mt: 1 }}>
          <Typography variant="subtitle1">{service.name}</Typography>
          <Typography variant="body2">Due Mileage: {service.dueMileage}</Typography>
          <Typography variant="body2">Status: {service.status}</Typography>
          <Typography variant="body2">Price: {service.price}</Typography>
        </Box>
      ))}

      {events.length === 0 && (
        <Typography variant="h6" sx={{ mt: 2, textAlign: 'center' }}>Say hi to Lana</Typography>
      )}

      <ButtonContainer>
        <StyledButton variant="contained" color="primary">Problem</StyledButton>
        <StyledButton
          variant="contained"
          color="primary"
          onClick={isSessionActive ? stopSession : startSession}
        >
          {isSessionActive ? "Finish Talk" : "Talk to Lana"}
        </StyledButton>
      </ButtonContainer>
    </Box>
  );
};

const ButtonContainer = styled(Box)(({ theme }) => ({
  position: 'fixed',
  bottom: 0,
  left: 0,
  right: 0,
  padding: theme.spacing(1),
  backgroundColor: theme.palette.background.paper,
  display: 'flex',
  justifyContent: 'center',
  flexDirection: 'row',
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
  },
}));

const StyledButton = styled(Button)(({ theme }) => ({
  flexGrow: 1,
  margin: theme.spacing(1),
  width: `calc(100% - 2 * ${theme.spacing(1)})`,
  padding: theme.spacing(2),
  fontSize: '1.2rem',
}));

export default ServicePage;